import {
	createComplaintChannel,
	type ComplaintChannelEntry,
} from '#app/routes/mocra+/_form+/adverse-events/form.ts'
import {
	CHANNEL_CARD_OPTIONS,
	COMPLAINT_CHANNEL_VALUE_SET,
} from '#app/routes/mocra+/_form+/adverse-events/helpers.ts'
import {
	CHANNEL_SYSTEM_FALLBACK_OPTIONS,
	COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

export type IntakeChannelValue = (typeof CHANNEL_CARD_OPTIONS)[number]['value']

export type IntakeChannelDraft = {
	channel: IntakeChannelValue
	ownerType: string
	ownerName: string
	system: string
}

const OWNER_TYPE_SET = new Set(
	COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS.map((option) => option.value),
)

export function isIntakeChannelValue(
	value: unknown,
): value is IntakeChannelValue {
	return typeof value === 'string' && COMPLAINT_CHANNEL_VALUE_SET.has(value)
}

export function createIntakeChannelDraft(
	value: IntakeChannelValue,
): IntakeChannelDraft {
	const entry = createComplaintChannel(value)
	return {
		channel: value,
		ownerType: entry.ownerType,
		ownerName: entry.ownerName,
		system: entry.system,
	}
}

export function sanitizeIntakeChannelDraft(
	draft: IntakeChannelDraft,
): IntakeChannelDraft {
	const channel = isIntakeChannelValue(draft.channel)
		? draft.channel
		: (CHANNEL_CARD_OPTIONS[0]?.value ?? 'hotline')

	const ownerType = OWNER_TYPE_SET.has(draft.ownerType)
		? draft.ownerType
		: (COMPLAINT_CHANNEL_OWNER_TYPE_OPTIONS[0]?.value ?? draft.ownerType)

	const fallbackMatch = CHANNEL_SYSTEM_FALLBACK_OPTIONS.find((option) => {
		const normalizedSystem = draft.system.trim()
		if (!normalizedSystem) return false
		return (
			option.value === normalizedSystem ||
			option.label === normalizedSystem ||
			option.value.toLowerCase() === normalizedSystem.toLowerCase() ||
			option.label.toLowerCase() === normalizedSystem.toLowerCase()
		)
	})

	const normalizedSystem = fallbackMatch
		? fallbackMatch.value
		: draft.system.trim()

	return {
		channel,
		ownerType,
		ownerName: draft.ownerName.trim(),
		system: normalizedSystem,
	}
}

export function collectIntakeChannelErrors(
	draft: IntakeChannelDraft,
): string[] {
	const sanitized = sanitizeIntakeChannelDraft(draft)
	const errors: string[] = []
	if (!OWNER_TYPE_SET.has(sanitized.ownerType)) {
		errors.push('Select the owner or operator for this channel.')
	}
	if (sanitized.ownerName.length === 0) {
		errors.push(
			'Add the retailer, platform, or team responsible for the channel.',
		)
	}
	if (sanitized.system.length === 0) {
		errors.push('Pick where complaints land after intake.')
	}
	return errors
}

export function intakeChannelDraftToEntry(
	draft: IntakeChannelDraft,
): ComplaintChannelEntry {
	const sanitized = sanitizeIntakeChannelDraft(draft)
	return {
		channel: sanitized.channel,
		ownerType: sanitized.ownerType,
		ownerName: sanitized.ownerName,
		system: sanitized.system,
	}
}

export function intakeChannelEntryToDraft(
	entry: ComplaintChannelEntry,
): IntakeChannelDraft {
	return sanitizeIntakeChannelDraft({
		channel: entry.channel as IntakeChannelValue,
		ownerType: entry.ownerType,
		ownerName: entry.ownerName,
		system: entry.system,
	})
}
